import React, { useEffect, useMemo, useState } from 'react';

import PageHero from '../components/PageHero';
import SectionHeader from '../components/SectionHeader';
import VideoCard from '../components/VideoCard';
import videos from '../data/videos';
import '../styles/pages/work.css';

const BOOK_DEMO = 'https://calendar.app.google/6g62S4xtRa8eWipt5';

// Restates the product's own promises (from the intro copy) for the ticker.
const promises = [
  'Runs on a computer you own',
  'No cloud',
  'No API keys',
  'No per-question billing',
  'No internet connection required',
  'Documents indexed on the same machine'
];

/**
 * Everything a visitor might reasonably search for, flattened once per video:
 * title, intro copy, the "What you'll see" items and the chapter labels. That
 * means "invite link" or "fault code" finds the right walkthrough even though
 * neither phrase appears in a title.
 */
const searchIndex = videos.map((video) => ({
  video,
  haystack: [
    video.title,
    ...video.paragraphs,
    ...(video.highlights ? video.highlights.items : []),
    ...(video.chapters ? video.chapters.map((c) => c.label) : []),
    video.callout || '',
    video.footnote || ''
  ]
    .join(' ')
    .toLowerCase()
}));

/**
 * Product page for MoneliLLM at /monelillm — the link to hand out in outreach.
 * Videos come from the same src/data/videos.js the /demos grid uses, so adding
 * a video shows up in both places.
 */
function MoneliLLM() {
  const [query, setQuery] = useState('');

  // Every whitespace-separated term must appear somewhere in the video, so
  // extra words narrow the results instead of widening them.
  const results = useMemo(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return videos;
    return searchIndex
      .filter(({ haystack }) => terms.every((term) => haystack.includes(term)))
      .map(({ video }) => video);
  }, [query]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'MoneliLLM — Private AI That Never Leaves Your Machine | Moneli Automation Inc.';

    const description =
      "MoneliLLM runs a real AI model on a computer you own. Your documents are indexed on that same machine, so your contracts, customer lists and trade secrets never leave the building. Watch the walkthroughs.";

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = description;

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'MoneliLLM',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'macOS',
      description,
      url: 'https://moneliautomation.com/monelillm',
      publisher: {
        '@type': 'Organization',
        name: 'Moneli Automation Inc.',
        url: 'https://moneliautomation.com',
        logo: 'https://moneliautomation.com/logo.png'
      }
    };

    let schemaScript = document.getElementById('monelillm-schema');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.id = 'monelillm-schema';
      schemaScript.type = 'application/ld+json';
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify(structuredData);

    return () => {
      document.title = 'Moneli Automation Inc.';
    };
  }, []);

  const trackVideoPlay = (video, startSeconds) => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'video_play', {
        video_title: video.title,
        video_id: video.youtubeId,
        start_seconds: startSeconds || 0,
        page: '/monelillm'
      });
    }
  };

  const hasQuery = Boolean(query.trim());

  return (
    <div className="work-page work-llm">
      <PageHero
        variant="dark"
        className="work-llm__hero"
        eyebrow="Private, on-device AI"
        title={<>MoneliLLM<span className="work-llm__tagline">Private AI that <em>never leaves</em> your machine.</span></>}
        lead={<>
          {/* Mobile gets one sentence so the first video is reachable without
              scrolling past a wall of text; desktop has the room for the full
              pitch. Only one is ever displayed. */}
          <span className="d-lg-none">
            The model runs on a computer you own — no cloud, no API keys, no per-question
            billing.
          </span>
          <span className="d-none d-lg-inline">
            Most AI tools send your questions and your files to somebody else's servers.
            MoneliLLM doesn't. The model runs on a computer you own and your documents are
            indexed on that same machine — no API keys, no per-question billing, and no
            internet connection required.
          </span>
        </>}
        actions={<>
          <a
            className="btn btn-primary btn-lg"
            href={BOOK_DEMO}
            target="_blank"
            rel="noopener noreferrer"
            data-magnetic
          >
            Book a Demo
          </a>
          <a className="btn btn-outline-light btn-lg work-llm__mail" href="mailto:info@moneliautomation.com">
            <i className="fa-solid fa-envelope" aria-hidden="true"></i> info@moneliautomation.com
          </a>
        </>}
      >
        {/* Decorative: everything stays inside one box (desktop only). */}
        <div className="work-llm__diagram" aria-hidden="true">
          <div className="work-llm__machine">
            <span className="work-llm__machine-label"><span className="dot-live" /> Your machine</span>
            <div className="work-llm__core">
              <span className="work-llm__ring work-llm__ring--1" />
              <span className="work-llm__ring work-llm__ring--2" />
              <span className="work-llm__chip"><i className="fa-solid fa-microchip" /></span>
            </div>
            <span className="work-llm__node work-llm__node--docs"><i className="fa-solid fa-file-lines" /> Your documents</span>
            <span className="work-llm__node work-llm__node--model"><i className="fa-solid fa-brain" /> Local model</span>
            <span className="work-llm__node work-llm__node--answers"><i className="fa-solid fa-comment-dots" /> Answers</span>
          </div>
          <div className="work-llm__cloud">
            <i className="fa-solid fa-cloud" />
            <span>No cloud</span>
          </div>
        </div>
      </PageHero>

      <section className="section section--sand section--flush" aria-label="What MoneliLLM does not need">
        <div className="marquee marquee--small marquee--fade work-llm__ticker" data-marquee data-speed="40">
          <div className="marquee__track">
            {promises.map((w) => (
              <span className="marquee__item" key={w}>{w}<span className="marquee__sep" aria-hidden="true" /></span>
            ))}
          </div>
        </div>
      </section>

      <section id="walkthroughs" className="section section--light work-anchor" aria-labelledby="walkthroughs-title">
        <div className="container">
          <SectionHeader
            eyebrow="Product walkthroughs"
            title={<>Watch it <em>work</em></>}
            lead={<>
              <span className="d-lg-none">{videos.length} walkthroughs of the real product.</span>
              <span className="d-none d-lg-inline">
                {videos.length} walkthroughs of the real product, in the order you'd watch them.
                Nothing loads from YouTube until you press play.
              </span>
            </>}
            id="walkthroughs-title"
          />

          <div className="video-search work-search" data-reveal>
            <label className="video-search__field">
              <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the walkthroughs — try “invite links” or “sources”"
                aria-label="Search the walkthroughs"
              />
              {query && (
                <button
                  type="button"
                  className="video-search__clear"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                </button>
              )}
            </label>
            {/* Announced to screen readers as results change. */}
            <p className="video-search__count" role="status">
              {hasQuery
                ? `${results.length} of ${videos.length} ${results.length === 1 ? 'video' : 'videos'}`
                : `${videos.length} videos`}
            </p>
          </div>

          {results.length > 0 ? (
            <div className={`work-videos${results.length % 2 ? ' work-videos--odd' : ''}`}>
              {results.map((video, i) => (
                <div className="work-videos__item" key={video.slug} data-reveal data-delay={String((i % 2) * 0.08)}>
                  <VideoCard video={video} onPlay={trackVideoPlay} />
                </div>
              ))}
            </div>
          ) : (
            <div className="video-search__empty work-search__empty">
              <span className="icon-tile icon-tile--note" aria-hidden="true">
                <i className="fa-solid fa-magnifying-glass"></i>
              </span>
              <p>Nothing matches “{query.trim()}”.</p>
              <button type="button" className="btn btn-ink" onClick={() => setQuery('')}>
                Show all videos
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default MoneliLLM;
